import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { BottomSheet } from '@vio/atlas/BottomSheet';
import { Button } from '@vio/atlas/Button';
import { ContentWrapper } from '@vio/atlas/helpers/ContentWrapper';
import { Flex } from '@vio/atlas/helpers/Flex';
import { Text } from '@vio/atlas/Text';
import { useShowAccommodationPage } from 'hooks/useShowAccommodationPage';
import { getCurrencyCode } from 'modules/meta/selectors';
import { getHotelId } from 'modules/search/selectors';
import { openSearchBox, setIsSearchBoxActive } from 'modules/searchBox/slice';
import { SearchBoxAncestor } from 'modules/searchBox/types';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { formatPriceValue } from '@daedalus/core/src/price/business/formatPrice';
const InlineFormattedMessage = /*#__PURE__*/styled('div')({
  name: "InlineFormattedMessage",
  class: "i122wfit",
  propsAsIs: false
});
const InlineFormattedMessageGap = /*#__PURE__*/styled('div')({
  name: "InlineFormattedMessageGap",
  class: "i8oi5cg",
  propsAsIs: false
});
const messages = defineMessages({
  priceIncrease: {
    id: 'nightPriceIncrease',
    defaultMessage: '{price}/night price increase'
  },
  priceDrop: {
    id: 'nightPriceDrop',
    defaultMessage: '{price}/night price drop'
  },
  priceIncreaseLastChecked: {
    id: 'lastCheckedIncreased',
    defaultMessage: 'The price here has gone up to {price} per night since we last checked.'
  },
  priceDropLastChecked: {
    id: 'lastCheckedDrop',
    defaultMessage: 'The price here has gone down to {price} per night since we last checked.'
  }
});
export interface PriceUpdateBottomSheetProps {
  isOpen?: boolean;
  isIncrease: boolean;
  alertPrice: number;
  currentPrice: number;
}
const TitleComponent = () => <ContentWrapper paddingY="s300">
    <Text variant="titleM">
      <FormattedMessageWrapper id="priceUpdate" defaultMessage="Price update" />
    </Text>
  </ContentWrapper>;
const PriceUpdateBottomSheet = ({
  isOpen,
  isIncrease,
  alertPrice = null,
  currentPrice = null
}: PriceUpdateBottomSheetProps) => {
  const dispatch = useDispatch();
  const {
    formatMessage,
    locale
  } = useIntl();
  const hotelId = useSelector(getHotelId);
  const currencyCode = useSelector(getCurrencyCode);
  const shouldShowBottomSheet = alertPrice !== null && currentPrice !== null;
  const [isBottomSheetOpen, setBottomSheetOpen] = useState(isOpen);
  const {
    showAccommodationPage
  } = useShowAccommodationPage();
  const priceDirection = isIncrease ? 'priceIncrease' : 'priceDrop';
  const trackingPayload = useMemo(() => ({
    type: priceDirection,
    alertPrice,
    currentPrice
  }), [priceDirection, alertPrice, currentPrice]);
  const handleClose = useCallback(() => {
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.PriceMismatch,
      action: Action.Closed,
      payload: {
        trackingPayload
      }
    }));
    setBottomSheetOpen(false);
  }, [dispatch, trackingPayload]);
  const handleStartSearch = useCallback(() => {
    dispatch(openSearchBox(SearchBoxAncestor.Page));
    dispatch(setIsSearchBoxActive(true));
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.PriceMismatchNewSearch,
      action: Action.Clicked,
      payload: {
        trackingPayload
      }
    }));
    setBottomSheetOpen(false);
  }, [dispatch, trackingPayload]);
  const handleViewDeal = useCallback(() => {
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.PriceMismatchViewDeal,
      action: Action.Clicked,
      payload: {
        trackingPayload
      }
    }));
    setBottomSheetOpen(false);
    showAccommodationPage({
      hotelId
    });
  }, [dispatch, trackingPayload, showAccommodationPage, hotelId]);
  useEffect(() => {
    if (shouldShowBottomSheet) {
      dispatch(trackEvent({
        category: Category.System,
        entity: Entity.PriceMismatch,
        action: Action.Displayed,
        payload: {
          trackingPayload
        }
      }));
    }
  }, [dispatch, shouldShowBottomSheet]);
  const price = useCallback((value: number) => formatPriceValue({
    locale,
    value,
    currency: currencyCode,
    maximumFractionDigits: 0
  }), [locale, currencyCode]);
  if (!shouldShowBottomSheet) return null;
  return <BottomSheet isOpen={isBottomSheetOpen} onClose={handleClose} titleBarProps={{
    centerContent: <TitleComponent />
  }} bottomBarContent={<Flex.Column data-id="starRatingsBottomSheetButtons" gap="s300">
          <Button size="xl" dataId="clearFiltersButton" onClick={handleViewDeal} fullWidth>
            <FormattedMessageWrapper id="viewDeal" defaultMessage="View Deal" />
          </Button>
          <Button size="xl" dataId="applyFiltersButton" onClick={handleStartSearch} variant="secondary" fullWidth>
            <FormattedMessageWrapper id="startNewSearch" defaultMessage="Start new search" />
          </Button>
        </Flex.Column>}>
      <ContentWrapper paddingTop="s400">
        <Text variant="titleL">
          <InlineFormattedMessage>
            {formatMessage(messages[priceDirection], {
            price: price(alertPrice)
          })}
          </InlineFormattedMessage>
        </Text>
        <Text variant="bodyM">
          <InlineFormattedMessageGap>
            {formatMessage(messages[`${priceDirection}LastChecked`], {
            price: price(currentPrice)
          })}
          </InlineFormattedMessageGap>
        </Text>
      </ContentWrapper>
    </BottomSheet>;
};
export default PriceUpdateBottomSheet;

require("./PriceUpdateBottomSheet.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./PriceUpdateBottomSheet.tsx");